import { useMemo, useState, useEffect } from 'react'
import { useQueries } from 'react-query'
import dayjs from 'dayjs'
import { uniqBy } from 'lodash'
import { type AlmanacFileIndexItem } from 'hooks/queries/files/types'
import { AlmanacFilesClient } from 'api/clients/AlmanacFilesClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { CalendarFilters } from './types'
import { AlmanacFileCalendarEvent } from './types'
import { filesKeys } from 'hooks/queries/files/filesKeys'
import { buildFilesQuery } from 'api/clients/AlmanacFilesClient'
import { ChannelType } from 'components/CampaignCreationModal/Types'

export const CALENDAR_V2_REFETCH_INTERVAL_MS = 60_000

interface UseCalendarParams {
  filters: CalendarFilters
  currentMonth: { year: number; month: number } | null
  options?: {
    include_design_content: boolean
    include_scheduled_post: boolean
    include_brand_plan_schedule_content: boolean
  }
}

export function useCalendar({ filters, currentMonth, options }: UseCalendarParams) {
  const workspaceId = useCurrentWorkspaceId()
  const calendarV2 = useFlipperFlag('calendar_v2')
  const currentMonthDate = useMemo(() => {
    return currentMonth && currentMonth.year !== null && currentMonth.month !== null
      ? dayjs(`${currentMonth.year}-${currentMonth.month + 1}-01`)
      : dayjs()
  }, [currentMonth])

  const getDateRange = (date: dayjs.Dayjs) => ({
    startDate: date.startOf('month').startOf('week').toISOString(),
    endDate: date.endOf('month').endOf('week').toISOString(),
  })

  // Define the months to fetch, starting with just current month
  const [monthsToFetch, setMonthsToFetch] = useState([{ offset: 0 }])

  // Use useQueries to fetch data for multiple months
  const monthQueries = useQueries(
    monthsToFetch.map(({ offset }) => {
      const date = currentMonthDate.add(offset, 'month')
      const range = getDateRange(date)
      const ransackQuery = buildFilesQuery({
        ...filters,
        startDate: range.startDate,
        endDate: range.endDate,
        sorting: 'post_at asc',
      })

      return {
        queryKey: filesKeys.calendar(workspaceId, {
          filters,
          startDate: range.startDate,
          endDate: range.endDate,
        }),
        queryFn: async () => {
          if (!workspaceId) {
            throw new Error('A workspace must be selected before fetching files.')
          }
          // Fetch all pages for this date range
          const allFiles: AlmanacFileIndexItem[] = []
          let page = 1
          let hasMore = true

          while (hasMore) {
            const response = await AlmanacFilesClient.index(workspaceId, {
              page,
              items: 1000,
              ...(calendarV2 ? { partial_calendar: true } : {}),
              q: ransackQuery,
            })
            if (response.type === SUCCESS_RESPONSE_TYPE) {
              allFiles.push(...response.data.almanac_files)
              hasMore = response.data.almanac_files.length === 1000
              page++
            } else {
              hasMore = false
            }
          }
          return allFiles
        },
        enabled: !!workspaceId,
        cacheTime: 30 * 60 * 1000, // 30 minutes
        refetchInterval: (data: AlmanacFileIndexItem[] | undefined) => {
          if (calendarV2) return CALENDAR_V2_REFETCH_INTERVAL_MS
          if (!data) return false
          const hasGenerating = data.some((file) => file.is_generating || file.image_url_generating)
          return hasGenerating ? 10_000 : false
        },
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        refetchOnMount: true,
      }
    }),
  )

  // After current month is fetched, add adjacent months
  const isCurrentMonthFetched = monthQueries[0]?.isFetched || false
  useEffect(() => {
    if (isCurrentMonthFetched && monthsToFetch.length === 1) {
      setMonthsToFetch([{ offset: 0 }, { offset: -1 }, { offset: 1 }])
    }
  }, [isCurrentMonthFetched, monthsToFetch.length])

  // Aggregate events from all months
  const eventsData = useMemo(() => {
    const allFiles = monthQueries.filter((query) => query.data).flatMap((query) => query.data as AlmanacFileIndexItem[])

    const calendarEvents: AlmanacFileCalendarEvent[] = allFiles.map((file) => mapFileToCalendarEvent(file))
    return uniqBy(calendarEvents, 'id')
  }, [monthQueries])

  const isLoading = monthQueries.some((query) => query.isLoading || query.isFetching)
  const hasInitialData = monthQueries[0]?.isFetched || false

  return {
    eventsData,
    hasInitialData,
    isLoading,
  }
}

export function mapFileToCalendarEvent(file: AlmanacFileIndexItem): AlmanacFileCalendarEvent {
  return {
    key: file.key,
    title: file.title || '',
    post_at: file.post_at || '',
    image_url: file.image_url || '',
    emoji: file.emoji || undefined,
    channel: file.channel || undefined,
    channel_emoji: (file.channel_emoji as ChannelType) || undefined,
    asset_type: file.asset_type || '',
    post_url: file.post_url || undefined,
    status: file.post_status || undefined,
    duration: file.duration || undefined,
    content: file.content || undefined,
    image_url_generating: file.image_url_generating || false,
    is_generating: file.is_generating || false,
    is_loading: file.is_loading,
    created_at: file.created_at || '',
    updated_at: file.updated_at || '',
    external_integration_id: file.external_integration_id || undefined,
    parent_id: file.parent_id || undefined,
    design_content: file.design_content || undefined,
    width: file.width || undefined,
    height: file.height || undefined,
    viewed_at: file.viewed_at || undefined,
    scheduled_post_targets: file.scheduled_post_targets || [],
    postable: false, // Not used in calendar, but required by type
    // Calendar event specific fields
    id: file.key,
    start: file.post_at || '',
    end: file.post_at || '',
    image: file.image_url || '',
    // Remove scheduled_post nesting - targets are directly on file
    scheduled_post: undefined,
    did_generation_fail: file.did_generation_fail || false,
    is_design_blank: file.is_design_blank || false,
    reviewed_at: file.reviewed_at || undefined,
    is_ai_generated_video: file.is_ai_generated_video || false,
    is_carousel: file.is_carousel,
    content_type: file.content_type,
    progress: file.progress || null,
    scheduleContentId: file.schedule_content_id?.toString() || null,
    reference_image_url: file.reference_image_url || null,
  }
}
